import moment from 'moment';
import { Model } from './model';
import { type BlobModel, CaseModel, EntryModel, type FormFieldChanges, OTHER_CHOICE_CODE, type StudyChanges } from '@/models';
import { generateHexIdentifier, getOnlyTimeSeconds, getRandomAdjective, getUTCSeconds, getUTCSecondsAtStartOfDay, LocalStorageUtils } from '@/utils';
import { getCurrentTimezone, getTimezone, type Timezone } from '@/utils/timezone';
import type { DrillData, DrillLevel } from '@/components/forms/form_builder/FormFieldConfigHelper';
import { getMatrixValues } from '@/components/data/entry/items/MatrixHelper';
import type { CustomIcon } from '@/components/common/interfaces/CustomIcon';
import { isAssignEffectPresent } from '@/components/data/entry/effects';
import type { DashboardCohortFilter } from './surgeonDashboard';
import { uuid } from 'vue-uuid';
import { DateTime } from 'luxon';

export interface NetworkForm {
  uri: string;
  hexIdentifier: string;
  identifier: string;
  creation: string;
  revisedOn: string;
  numFields: number;
  edit: boolean;
  title: string;
  investigationIdentifier?: string;

  enforcements?: FormEnforcements;
  items?: FormField[];
  anonymousResponsesAllowed?: boolean;
  editExistingEntry?: boolean;
  reminders?: NetworkReminders;
  translations?: FormTranslation[];

  sharedForm?: boolean;

  entries?: EntryModel[];
  welcomeScreenTemplate?: string;
  archivedAt?: string;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  timeSpentData?: TimeSpentOnForm;
  multipleEntriesError?: string;
  cachePublicEntry?: boolean;

  // Message to display when the form has data entry closed (closeEntry
  // enforcement). The closeEntry flag itself travels in `enforcements`.
  closedMessage?: string;

  /** Only on `GET form/:id`, and only when the request names a case. */
  links?: unknown;
}

export interface TimeSpentOnForm {
  averageEntryTime: number;
  minEntryTime: number;
  maxEntryTime: number;
}

interface NetworkReminders {
  type: string;
  recurrence: string;
  randomness: string | null;
  timezone: string | Timezone;
  ends: string | null;
  notification: ReminderNotification
}

interface FormStudy {
  identifier: string;
  scrambled: boolean;
}

export interface PostForm {
  title: string;
  studies: FormStudy[];
  description?: string;
  revisionDate: number;
  items: FormField[];
  anonymousResponsesAllowed: boolean;
  reminders?: Reminders;
  editExistingEntry?: boolean;
}

export interface PostFormResponse {
  identifier: string;
}

export interface PostFormFile {
  filename: string;
  filetype: string;
  itemIdentifier?: string;
  size: number;
  withoutItemId: boolean;
}

export interface PresignedPostFields {
  key: string;
  bucket: string;
  'X-Amz-Algorithm': string;
  'X-Amz-Credential': string;
  'X-Amz-Date': string;
  Policy: string;
  'X-Amz-Signature': string;
}

export interface PresignedPost {
  url: string;
  fields: PresignedPostFields
}

export interface FormFileSignedResponse {
  presignedPost: PresignedPost;
  blob: NetworkBlob;
}

/**
 * One requirement a case has to meet before a form opens to it, named rather than encoded.
 *
 * The study states these as a condition tree over field and form *identifiers*
 * (`["==","@176b68","yes","#d03b269ece8acfa9"]`). Resolving those to a question and a form title
 * takes the study's form fields, which the pipeline's case query already holds and no client
 * response carries — so the API names them and this is what arrives.
 */
export interface FormRequirement {
  /** The title of the form the answer is expected on. */
  form: string;
  /**
   * The same form under the key the case list files its entries and enforcements by, so the card
   * can open it without matching on a title two forms are free to share. Absent from an API built
   * before the requirements became links, which leaves the row as text.
   */
  formIdentifier?: string;
  /**
   * The entry the condition read its answer from: the case's first on that form, which is the one
   * the API evaluates, so an answer given on any other entry of it would open nothing. Absent where
   * the case has not started the form, which is itself why the condition fails.
   */
  entryIdentifier?: string;
  /** The question's label, as the form asks it. */
  field: string;
  /** The comparison, as the raw operator — `==`, `!=`, `>`, `⊂=` and so on. The wording is ours. */
  operator: string;
  /** The answer it wants, already resolved to a choice's label where the field has choices. */
  value: string;
  /**
   * The field's own type (`Date`, `Choice`, `Number`, …), where the study still has the field.
   *
   * The operator alone does not say how to word a requirement. A study writes "the consent date is
   * filled in" as `> 0`, and "is more than 0" is not a sentence anybody can act on.
   */
  fieldType?: string;
}

/**
 * The requirements a case fails, with the shape of the study's condition kept.
 *
 * A flat list cannot say what `A && (B || C)` says, and the study this was written against says
 * exactly that: the liver was transplanted, *and* either the IRB waiver pair or the consent pair is
 * filled in. Flattened, the card listed five requirements under "all of these", three of which
 * nobody has to do.
 *
 * Already pruned by the time it arrives — a met branch is dropped, a group left with one child
 * arrives as that child — so every node here is something still outstanding.
 */
export type FormRequirementNode =
  /** Every child has to hold — the study's `&&`. */
  | { kind: 'all'; children: FormRequirementNode[] }
  /** Any one child opens the form — the study's `||`. */
  | { kind: 'any'; children: FormRequirementNode[] }
  /** One question and the answer it wants. */
  | ({ kind: 'requirement' } & FormRequirement);

/**
 * Why a form is closed to this case.
 *
 * `hidden` below is one boolean over two unrelated facts — a designer switched the form off for the
 * whole study, or this case's answers do not satisfy its `Show` condition — and until this arrived
 * the grid could only draw both as the same hatched cell saying "Not collected for this case".
 * `closeEntry` has no reason here on purpose: it does not close a pipeline cell, so giving it one
 * would change which cells are hatched rather than explain the ones that already are.
 */
export interface FormBlock {
  reason: 'hidden' | 'condition';
  /**
   * What the case still has to answer. Only set on `condition`, and absent there too where the
   * condition compares things the API cannot name — a leaf pointing at a deleted form or field, a
   * negated group — so nothing may assume it is present.
   */
  requirements?: FormRequirementNode;
}

export interface FormEnforcements {
  enforceOrder: boolean;
  hidden: boolean;
  onlyOnce: boolean;
  order: number;
  allowEntry: boolean;
  // When true, data entry for this form is closed for everyone (link, UI, SP team).
  closeEntry?: boolean;
  /**
   * Why `hidden` is true, where the API could say. Absent on a form nothing closes, and absent on
   * any response built before ENG-8665 — so every reader treats it as optional and falls back to
   * the unqualified wording.
   */
  blocked?: FormBlock;
  /**
   * The form links a study admin set on this form, as stored. Not a reminder: `reminders` is the
   * scheduled email and SMS. `PATCH /survey/enforcement` stores whatever it is given, so read this
   * through `readFormLinks` rather than trusting its type.
   */
  links?: FormLink[];
}

/**
 * A message on one form's entry pointing the reader to another form of the same case. `type` is
 * there so a later kind of link can be stored beside this one; every reader skips a type it does
 * not know, so that link cannot break an older client.
 */
export interface FormLink {
  type: 'form';
  /** The target form, in hex. */
  formIdentifier: string;
  message: string;
}

/**
 * A form link as `GET form/:id` resolves it for one case. The API only sends the ones the case may
 * open, and only when the request names a case, so there is nothing left for a client to filter.
 */
export interface ResolvedFormLink extends FormLink {
  formTitle: string;
  /** The case's latest entry on the target form, or `null` to open a new one. */
  entryIdentifier: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isFormLink = (value: unknown): value is FormLink =>
  isRecord(value) &&
  value.type === 'form' &&
  typeof value.formIdentifier === 'string' &&
  value.formIdentifier !== '' &&
  typeof value.message === 'string';

const isResolvedFormLink = (value: unknown): value is ResolvedFormLink =>
  isFormLink(value) &&
  typeof (value as ResolvedFormLink).formTitle === 'string' &&
  ((value as ResolvedFormLink).entryIdentifier === null ||
    typeof (value as ResolvedFormLink).entryIdentifier === 'string');

/** The form links in `enforcements.links`, without any this client cannot read. */
export const readFormLinks = (links: unknown): FormLink[] =>
  Array.isArray(links) ? links.filter(isFormLink) : [];

/** The resolved links on a `GET form/:id` response, without any this client cannot read. */
export const readResolvedFormLinks = (links: unknown): ResolvedFormLink[] =>
  Array.isArray(links) ? links.filter(isResolvedFormLink) : [];

interface ReminderNotification {
  subject: string;
  body: string;
}

export interface Reminders {
  type: string;
  recurrence: string;
  randomness: string | null;
  timezone: Timezone;
  ends: string | null;
  notification: ReminderNotification
}

export interface FormTranslation {
  languageCode: string;
  language: string;
  title: string;
  description: string | null;
  items?: FormField[];
}

export interface IForm {
  identifier: string;
  createdAt: Date;
  updatedAt: Date;
  title: string;
  base64Id?: string;
  investigationIdentifier?: string;
  fieldCount: number;
  enforcements: FormEnforcements;
  anonymousResponsesAllowed?: boolean;
  editExistingEntry?: boolean;
  reminders?: Reminders;
  formTranslations?: FormTranslation[];

  sharedForm?: boolean;
  welcomeScreenTemplate?: string;
  archivedAt?: string;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  timeSpentData?: TimeSpentOnForm;

  multipleEntriesError?: string;
  cachePublicEntry?: boolean;
}

export class FormModel extends Model {
  identifier: string;
  createdAt: Date;
  updatedAt: Date;
  archivedAt?: Date;
  title: string;
  base64Id?: string;
  fieldCount: number;
  enforcements: FormEnforcements;
  studyId?: string;

  order?: number;
  items?: FormField[];
  anonymousResponsesAllowed: boolean;
  editExistingEntry?: boolean;
  reminders?: Reminders;
  formTranslations?: FormTranslation[];

  sharedForm?: boolean;

  entries?: EntryModel[];
  welcomeScreenTemplate?: string;
  selectedCase?: CaseModel;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  timeSpentData?: TimeSpentOnForm;
  multipleEntriesError?: string;
  cachePublicEntry?: boolean;

  // Message shown when the backend reports the form's data entry is closed.
  closedMessage?: string;

  /** The form links this form's case may follow. Empty wherever the form was read without a case. */
  links: ResolvedFormLink[] = [];

  constructor({
    identifier,
    createdAt,
    updatedAt,
    title,
    base64Id,
    fieldCount,
    enforcements,
    anonymousResponsesAllowed,
    editExistingEntry,
    reminders,
    formTranslations,
    sharedForm,
    welcomeScreenTemplate,
    entrySaveButtonText,
    showSaveEntryButtonAtBottom,
    hasEntryPermission,
    investigationIdentifier,
    archivedAt,
    timeSpentData,
    multipleEntriesError,
    cachePublicEntry
  }: IForm) {
    super();
    this.identifier = identifier;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.title = title;
    this.base64Id = base64Id;
    this.fieldCount = fieldCount;
    this.enforcements = enforcements;
    this.anonymousResponsesAllowed = anonymousResponsesAllowed == true;
    this.editExistingEntry = editExistingEntry;
    this.reminders = reminders;
    this.formTranslations = formTranslations;
    this.sharedForm = sharedForm;
    this.welcomeScreenTemplate = welcomeScreenTemplate;
    this.entrySaveButtonText = entrySaveButtonText;
    this.showSaveEntryButtonAtBottom = showSaveEntryButtonAtBottom;
    this.hasEntryPermission = hasEntryPermission;
    this.studyId = investigationIdentifier;
    this.archivedAt = archivedAt ? moment(archivedAt).toDate() : undefined;
    this.timeSpentData = timeSpentData;
    this.multipleEntriesError = multipleEntriesError;
    this.cachePublicEntry = cachePublicEntry;
  }

  static mapFromBackend(form: NetworkForm) {
    if (form.reminders) {
      // One call for both shapes. `getTimezone` takes a name or an already-described `Timezone` —
      // and the branch that read `.value` threw on a reminder config that came back without a zone,
      // which fails every form in the study rather than one reminder.
      form.reminders.timezone = getTimezone(form.reminders.timezone);
    }

    const formModel = new FormModel({
      identifier: form.hexIdentifier ? form.hexIdentifier : form.identifier,
      createdAt: moment(form.creation).toDate(),
      updatedAt: moment(form.revisedOn).toDate(),
      title: form.title,
      base64Id: form.identifier,
      fieldCount: form.numFields,
      enforcements: form.enforcements ? form.enforcements : {
        enforceOrder: false,
        hidden: false,
        onlyOnce: false,
        order: 0,
        allowEntry: true
      },
      anonymousResponsesAllowed: form.anonymousResponsesAllowed,
      editExistingEntry: form.editExistingEntry,
      reminders: form.reminders as Reminders,
      formTranslations: form.translations,
      sharedForm: form.sharedForm,
      welcomeScreenTemplate: form.welcomeScreenTemplate,
      entrySaveButtonText: form.entrySaveButtonText,
      investigationIdentifier: form.investigationIdentifier,
      showSaveEntryButtonAtBottom: form.showSaveEntryButtonAtBottom,
      hasEntryPermission: form.hasEntryPermission,
      timeSpentData: form.timeSpentData,
      multipleEntriesError: form.multipleEntriesError,
      cachePublicEntry: form.cachePublicEntry
    });
    const items: FormField[] = [];
    if (form.items) {
      for (const item of form.items) {
        if (item.type === FormFieldType.CHOICE || item.type === FormFieldType.DROPDOWN) {
          if (typeof item.options.maximumChoicesCount === 'string') {
            item.options.maximumChoicesCount = parseInt(item.options.maximumChoicesCount);
          }
          item.options.maximumChoicesCount = item.options.maximumChoicesCount ? item.options.maximumChoicesCount : 2;
          item.options.allowOther = item.options.allowOther === true;

          if (item.options.choices) {
            let id = 1;
            item.options.choices.forEach(choice => {
              choice.id = id;
              id++;
            });
          }

          if (item.options.allowOther) {
            item.otherChoiceValue = {
              id: 99999999,
              label: 'Other',
              value: '-1',
              otherValue: ''
            };
          }
        }

        if (item.type === FormFieldType.FILE) {
          item.options.maximumSize = {
            enabled: item.options.maximumSize != null,
            value: item.options.maximumSize != null ? (item.options.maximumSize as number) : null
          };
          item.options.maximumFileNumber = {
            enabled: item.options.maximumFileNumber != null,
            value: item.options.maximumFileNumber != null ? (item.options.maximumFileNumber as number) : null
          };
          item.options.allowedExtensions = {
            enabled: item.options.allowedExtensions != null,
            value: item.options.allowedExtensions != null ? (item.options.allowedExtensions as string) : ''
          };
        }

        if (item.options.requirement) {
          item.strictRequired = item.options.requirement === FIELD_REQUIREMENTS.STRICT_REQUIRED;
          item.nonStrictRequired = item.options.requirement === FIELD_REQUIREMENTS.NON_STRICT_REQUIRED;
          item.optional = item.options.requirement === FIELD_REQUIREMENTS.OPTIONAL;
          item.recommended = item.options.requirement === FIELD_REQUIREMENTS.RECOMMENDED;
        }

        if (item.type === FormFieldType.SECTION) {
          item.sectionExpanded = !item.options.collapsedInitially;

          item.options.fields?.forEach(field => {
            const foundField = form.items!.find(formItem => formItem.identifier == field);
            if (foundField != null) {
              foundField.sectionId = item.identifier;
            }
          });

          const sectionFields = item.options.fields;
          sectionFields?.forEach(fieldItem => {
            const foundField = form.items!.find(field => field.identifier === fieldItem);
            if (foundField) {
              foundField.expanded = item.sectionExpanded;
            }
          });
        }

        if (item.type === FormFieldType.UNIT && item.options.choices && item.options.choices.length > 0) {
          item.options.choices.push({
            id: 10,
            label: 'Clear',
            value: 'clear'
          });
        }

        if (isAssignEffectPresent(item)) {
          item.disable = true;
        }

        if (item.expanded != false) {
          item.expanded = true;
        }
        items.push(item);
      }
    }
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    formModel.items = items;
    formModel.order = form.enforcements?.order;
    formModel.closedMessage = form.closedMessage;
    formModel.links = readResolvedFormLinks(form.links);
    return formModel;
  }

  static mapFormWithEntries(form: NetworkForm) {
    const formModel = new FormModel({
      identifier: form.hexIdentifier ? form.hexIdentifier : form.identifier,
      createdAt: moment(form.creation).toDate(),
      updatedAt: moment(form.revisedOn).toDate(),
      title: form.title,
      base64Id: form.identifier,
      fieldCount: form.numFields,
      enforcements: form.enforcements ? form.enforcements : {
        enforceOrder: false,
        hidden: false,
        onlyOnce: false,
        order: 0,
        allowEntry: true
      }
    });

    form.entries?.forEach(entry => {
      entry.createdAt = moment(entry.createdAt).toDate();
    });

    formModel.entries = form.entries;

    return formModel;
  }

  mapItemValues(updatedItems: Record<string, string | number>) {
    this.items?.forEach(field => {
      let value : string | number = field.value ? field.value : '';

      switch (field.type) {
        case FormFieldType.TEXT:
          value = field.value ? field.value : '';
          break;

        case FormFieldType.NUMBER:
          if (field.options.range) {
            value = field.value ? field.value : '';
          } else {
            value = field.numberValue !== undefined && field.numberValue !== null ? field.numberValue : '';
          }
          break;

        case FormFieldType.CHOICE:
          if (field.options.maximumChoicesCount == 1) {
            value = (field.choiceValue as FormFieldChoice)?.value;
            if (!value && field.options.allowOther && field.otherChoiceValue?.value === OTHER_CHOICE_CODE) {
              value = field.otherChoiceValue?.otherValue ?? '';
            }
          } else {
            const selectedChoices: string[] = [];
            field.options.choices?.forEach(choice => {
              if (choice.checked) {
                selectedChoices.push(`${choice.value}`);
              }
            });

            if (field.options.allowOther && field.otherChoiceValue?.checked) {
              selectedChoices.push(field.otherChoiceValue?.otherValue ?? '');
            }

            value = selectedChoices.join(',');
          }
          break;

        case FormFieldType.DROPDOWN:
          if (field.options.maximumChoicesCount == 1) {
            value = (field.choiceValue as FormFieldChoice)?.value;
          } else {
            const selectedChoices: string[] = [];
            (field.choiceValue as FormFieldChoice[])?.forEach(choice => {
              selectedChoices.push(`${choice.value}`);
            });

            value = selectedChoices.join(',');
          }
          break;

        case FormFieldType.DATE:
          if (field.dateValue) {
            if (field.options.mode === DateMode.DATE) {
              value = getUTCSecondsAtStartOfDay(field.dateValue);
            } else if (field.options.mode === DateMode.DATE_TIME) {
              if (field.options.absoluteDate) {
                value = getUTCSeconds(field.dateValue);
              } else {
                value = DateTime.fromJSDate(field.dateValue).toSeconds();
              }
              // Save the timezone info regardless of absolute date or not
              updatedItems[`${field.identifier}_data`] = JSON.stringify(field.timezoneRecorded);
            } else {
              value = getOnlyTimeSeconds(field.dateValue);
            }
          }
          break;

        case FormFieldType.LOCATION:

          if (field.locationValue) {
            // eslint-disable-next-line no-case-declarations
            const locationValue : NetworkLocationData = {
              latitude: field.locationValue.coords.latitude,
              longitude: field.locationValue.coords.longitude,
              accuracy: field.locationValue.coords.accuracy,
              timestamp: field.locationValue.timestamp
            };

            value = JSON.stringify(locationValue);
          }
          break;

        case FormFieldType.SLIDER:
          value = field.numberValue !== undefined && field.numberValue !== null ? field.numberValue : '';
          break;

        case FormFieldType.SIGNATURE:
          value = field.signatureValue ? JSON.stringify(field.signatureValue) : '';
          break;

        case FormFieldType.MATRIX:
          if (field.matrixTableRowValues) {
            // eslint-disable-next-line no-case-declarations
            const matrixValues = getMatrixValues(field.matrixTableRowValues);
            value = matrixValues ? JSON.stringify(matrixValues) : '';
          }
          break;

        case FormFieldType.FILE:
          if (field.fileIds) {
            value = JSON.stringify(field.fileIds);
          }
          break;

        case FormFieldType.DRILLDOWN:
          // eslint-disable-next-line no-case-declarations
          const selectedDrillLevels: string[] = [];
          if (field.drillValue && field.drillValue.length > 0) {
            (field.drillValue as DrillData[])!.forEach(level => {
              selectedDrillLevels.push(level.label);
            });
          }
          value = selectedDrillLevels.join(',');
          break;

        case FormFieldType.UNIT:
          if ((field.numberValue !== undefined && field.numberValue !== null) || field.choiceValue) {
            value = JSON.stringify({
              value: field.numberValue !== null ? field.numberValue : undefined,
              unit: field.value,
              inequality: (field.choiceValue as FormFieldChoice)?.value
            });
          } else {
            value = '';
          }
          break;
        default:
          break;
      }

      updatedItems[field.identifier] = value;
    });
  }
}

export interface PatchForm {
  surveyIdentifier: string;
  enforcements: FormEnforcements
}

export interface PatchFormsOrder {
  investigationId: string;
  surveys: PatchForm[]
}

export interface FormFieldGraphData {
  yAxisMin?: number;
  yAxisMax?: number;
  yAxisStep?: number;
  xAxisMin?: number;
  xAxisMax?: number;
  xAxisStep?: number;
}

export interface FormField {
  id: string;
  identifier: string;
  type: FormFieldType;
  label: string;
  description: string;
  conditions?: object;
  options: FormFieldOptions;
  formulaString: string;
  default: string;
  variableName?: string;

  icon?: CustomIcon;

  internalId?: string;
  expanded?: boolean;

  value?: string;
  numberValue?: number | null;
  choiceValue?: FormFieldChoice[] | FormFieldChoice;
  otherChoiceValue?: FormFieldChoice;
  dateValue?: Date;
  timezoneRecorded?: { timezone: string, overrideCalculatedValue?: boolean };
  overrideCalculatedValue?: boolean;
  locationValue?: GeolocationPosition;
  signatureValue?: SignatureData;
  fileIds?: string[];
  images?: {label: string, value: string}[];
  files?: BlobModel[];

  // matrix field related
  matrixValues?: MatrixValue[];
  matrixTableRowValues?: MatrixTableRow[];
  hasExistingMatrixValue?: boolean;
  matrixDependencyResolved?: number;

  drillValue?: DrillData[] | string[];

  //effects
  show?: boolean;
  warn?: string;
  error?: string;
  valid?: string;
  disable?: boolean;
  strictRequired?: boolean;
  nonStrictRequired?: boolean;
  optional?: boolean;
  recommended?: boolean;

  showRequiredError?: boolean;
  inputChanged?: boolean;

  invalidInput?: boolean;

  //import related
  changes?: FormFieldChanges;
  errors?: StudyChanges;

  blink?: boolean;

  sectionExpanded?: boolean;
  sectionId?: string;
  lastSectionField?: boolean;

  timeSeriesData?: TimeSeriesData[];
  historicalData?: TimeSeriesData[];
  centerHistoricData?: TimeSeriesData[];
  bicarbonateData?: TimeSeriesData[];
  glucoseData?: TimeSeriesData[];
  tpnData?: TimeSeriesData[];
  cumulativeTimeSeriesData?: TimeSeriesData[];


  //graph related
  filters?: DashboardCohortFilter[];
  liverCount?: number;
  centerLiverCount?: number;
  plotType?: string;
  order?: number;

  yAxisLabel?: string;
  perLiverKgOptionChecked?: boolean;

  loading?: boolean;
  showPerLiverKg?: boolean;

  graphData?: FormFieldGraphData;
  dataVersion?: number;
  graphVariable?: string;
}

export type MedicationType = 'bolus' | 'infusion';

export interface TimeSeriesData {
  perfusionSelectedTimepoint?: string;
  perfusionActualTimepoint: number;
  medicationType?: MedicationType;
  value: number;
  ymin: number;
  ymax: number;
  ymin2?: number;
  ymax2?: number;
  /**
   * The dose given, on the intervention series (`glucoseData`, `tpnData`, `bicarbonateData`).
   * A dose marker plots at the reading it was given against, so `value` is not the dose and
   * this is what the tooltip reads instead.
   *
   * Declared rather than reached for through a `@ts-expect-error`, which is how it was read
   * before ENG-8153 — in four places, each one silently outside the type.
   */
  volume?: string | number;
}

export class FormFieldChoice {
  id: number;
  label: string;
  value: string | number;
  tooltip?: string;

  // for the multiple choice questions
  checked?: boolean;
  otherValue?: string;
  archived?: boolean;
  variableName?: string;

  constructor(choice: IFormFieldChoice) {
    this.id = choice.id;
    this.label = choice.label;
    this.value = choice.value;
    this.tooltip = choice.tooltip;
    this.checked = choice.checked;
    this.otherValue = choice.otherValue;
    this.archived = choice.archived;
    this.variableName = choice.variableName;
  }

  static createEmpty() {
    return new FormFieldChoice({
      id: 0,
      label: '',
      value: ''
    });
  }
}

export interface IFormFieldChoice {
  id: number;
  label: string;
  value: string | number;
  tooltip?: string;

  // for the multiple choice questions
  checked?: boolean;
  otherValue?: string;
  archived?: boolean;
  variableName?: string;
}

export interface FileConfig {
  enabled: boolean;
  value: string | number | null;
}

export interface MatrixValue {
  rowId: string;
  values: MatrixColumnValue[];
  dynamicRows?: string[]
}

export interface MatrixColumnValue {
  id: string;
  value: string | number | boolean;
  timezoneRecorded?: { timezone: string } | string;
  conditions?: object;

  dateShifting?: boolean;
}

export interface MatrixRow {
  id: number;
  label: string;
  dynamicRowConfig?: DynamicRowConfig
  variableName?: string;
}

export interface DynamicRowConfig {
  copyRowData?: boolean;
  copyRowHeader?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conditions?: any[];
  rowsAdded?: number;
}

export interface MatrixTableRow {
  rowId: string;
  rowHeader: string;
  dynamicRowConfig?: DynamicRowConfig;

  dynamicRows?: string[];
  enableClear?: boolean;
  columnValues?: MatrixColumn[]
}

interface DynamicRowColumnFormula {
  columnId: number;
  operator: string;
}

interface ColumnFormula {
  columnId: number;
  rowId: number;
  destColumnId: number;
  operator: string;
}

export interface MatrixColumn {
  id: number;
  label: string;
  type: MatrixColumnType;
  tintColor?: string;
  options?: MatrixColumnOptions;

  disable?: boolean;
  key?: string;
  dynamicRowColumnFormula?: DynamicRowColumnFormula
  columnFormula?: ColumnFormula

  textValue?: string;
  numberValue?: number | null;
  booleanValue?: boolean;
  dateValue?: Date;
  unShiftedDate?: Date;
  timezoneRecorded?: { timezone: string };
  choiceValue?: FormFieldChoice[] | FormFieldChoice;

  //table column related
  frozen?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default?: any[];

  tooltip?: string;

  variableName?: string;

  conditions?: object;

  //file realted
  fileIds?: string[];

  warn?: string;
}

export interface MatrixFieldUploadedFile {
  identifier: string;
  rowId: string;
  columnId: string;
  files: BlobModel[];
}

interface UnitConfig {
  list: Unit[],
  default?: number,
  defaultFixed?: boolean
}

interface Unit {
  id: number;
  unit: string;
  description: string;
  conversions: object;
  fromFieldValue?: string;
}

export interface NetworkBlob {
  identifier: string;
  name: string;
  contentType: string;
  size: number;
  storedAt: string;
  hasThumbnail: boolean;
  thumbnailKey?: string;
  hasDisplayCopy?: boolean;
  conversionFailed?: boolean;
}

export interface NumberRange {
  rangeStart: number;
  rangeEnd: number;
}

interface FormFieldOptions {
  choices?: FormFieldChoice[];
  maximumChoicesCount?: number;
  allowOther?: boolean;
  mode?: DateMode | MatrixMode;
  absoluteDate?: boolean;
  dateShifting?: boolean;
  dateConstraints?: { min?: number | string; max?: number | string; };
  allowedExtensions?: FileConfig | string;
  maximumSize?: FileConfig | number;
  maximumFileNumber?: FileConfig | number;
  incrementSize?: number;
  startValue?: number;
  endValue?: number;
  startLabel?: string;
  endLabel?: string;
  numberOfSignatories?: string;
  allowEdit?: boolean;

  rows?: MatrixRow[];
  columns?: MatrixColumn[];

  //drilldown
  file?: string;
  drillLevels?: DrillLevel[];
  blob?: BlobModel;

  //new fields related
  units?: UnitConfig;
  format?: string;
  timeFormat?: number;

  requirement?: FIELD_REQUIREMENTS;

  fields?: string[];
  collapsedInitially?: boolean;

  overrideCalculation?: boolean;

  // Label tooltip
  tooltip?: string;


  range?: NumberRange
}

// eslint-disable-next-line no-shadow
export enum FIELD_REQUIREMENTS {
  STRICT_REQUIRED='STRICT_REQUIRED',
  NON_STRICT_REQUIRED='NON_STRICT_REQUIRED',
  OPTIONAL='OPTIONAL',
  RECOMMENDED='RECOMMENDED'
}

// eslint-disable-next-line no-shadow
export enum FormFieldType {
  NUMBER = 'Number',
  TEXT = 'Text',
  CHOICE = 'Choice',
  DROPDOWN = 'Dropdown',
  DATE = 'Date',
  LOCATION = 'Location',
  FILE = 'File',
  TITLE = 'Title',
  SLIDER = 'Slider',
  BARCODE = 'Barcode',
  SIGNATURE = 'Signature',
  MATRIX = 'Matrix',
  DRILLDOWN = 'Drilldown',

  // New fields
  UNIT='Unit',
  SECTION='Section',

  //Client only types
  BRANDING='Branding',
  SUBMIT_BUTTON='SubmitButton'
}

// eslint-disable-next-line no-shadow
export enum DateMode {
  DATE = 'date',
  TIME = 'time',
  DATE_TIME = 'datetime'
}

// eslint-disable-next-line no-shadow
export enum MatrixMode {
  RANK = 'rank',
}

export const FIELD_TYPES = [
  {
    id: 0,
    type: FormFieldType.NUMBER,
    label: 'Number',
    iconStyle: 'far',
    icon: 'hashtag'
  },
  {
    id: 1,
    type: FormFieldType.TEXT,
    label: 'Text',
    iconStyle: 'far',
    icon: 'text'
  },
  {
    id: 2,
    type: FormFieldType.CHOICE,
    label: 'Choice',
    iconStyle: 'far',
    icon: 'list-ul'
  },
  {
    id: 3,
    type: FormFieldType.DROPDOWN,
    label: 'Dropdown',
    iconStyle: 'far',
    icon: 'square-caret-down'
  },
  {
    id: 4,
    type: FormFieldType.DATE,
    label: 'Date',
    iconStyle: 'far',
    icon: 'calendar-days'
  },
  {
    id: 5,
    type: FormFieldType.LOCATION,
    label: 'GPS Location',
    iconStyle: 'far',
    icon: 'location-crosshairs'
  },
  {
    id: 6,
    type: FormFieldType.FILE,
    label: 'File',
    iconStyle: 'far',
    icon: 'file-arrow-up'
  },
  {
    id: 7,
    type: FormFieldType.TITLE,
    label: 'Title',
    iconStyle: 'far',
    icon: 'quote-left'
  },
  {
    id: 8,
    type: FormFieldType.SLIDER,
    label: 'Slider',
    iconStyle: 'far',
    icon: 'square-sliders'
  },
  {
    id: 9,
    type: FormFieldType.BARCODE,
    label: 'Barcode',
    iconStyle: 'far',
    icon: 'barcode-read'
  },
  {
    id: 10,
    type: FormFieldType.SIGNATURE,
    label: 'Signature',
    iconStyle: 'far',
    icon: 'file-signature'
  },
  {
    id: 11,
    type: FormFieldType.MATRIX,
    label: 'Matrix',
    iconStyle: 'fas',
    icon: 'table'
  },
  {
    id: 12,
    type: FormFieldType.DRILLDOWN,
    label: 'Drilldown',
    iconStyle: 'far',
    icon: 'list-tree'
  },
  {
    id: 13,
    type: FormFieldType.UNIT,
    label: 'Unit',
    iconStyle: 'far',
    icon: 'weight-hanging'
  },
  {
    id: 14,
    type: FormFieldType.SECTION,
    label: 'Section',
    iconStyle: 'fal',
    icon: 'file-half-dashed'
  }
];

export const createSampleFormPayload = (studyIdentifier: string): PostForm => {
  const username = LocalStorageUtils.getUserKeys().name?.split(' ')[0] || 'User';
  const title = `${username}'s ${getRandomAdjective()} form`;

  const studies = [{
    identifier: studyIdentifier,
    scrambled: true
  }];

  const item = createFormField();
  item.expanded = undefined;

  return {
    studies,
    title,
    revisionDate: new Date().getTime() / 1000,
    items: [item],
    description: '',
    anonymousResponsesAllowed: false
  };
};

export const createFormField = (): FormField => {
  return {
    id: uuid.v4(),
    identifier: generateHexIdentifier(6),
    type: FormFieldType.NUMBER,
    label: 'Example number field',
    description: '',
    conditions: [],
    options: {},
    formulaString: '',
    default: '',
    expanded: true
  };
};

export const getEmptyReminderConfig = () => ({
  type: 'rolling',
  recurrence: '0 10 * * *',
  randomness: null,
  timezone: getCurrentTimezone(),
  ends: null,
  notification: {
    subject: 'Time to complete a form',
    body: 'Don\'t forgot to log your data'
  }
});

// eslint-disable-next-line no-shadow
export enum MatrixColumnType {
  TEXT = 'Text',
  NUMBER = 'Number',
  SINGLE_CHOICE = 'RadioButton',
  MULTIPLE_CHOICE = 'Checkbox',
  DATE = 'Date',
  DROPDOWN = 'Dropdown',
  FILE = 'File',
}

export const MATRIX_COLUMN_TYPES = [
  {
    id: 0,
    type: MatrixColumnType.TEXT,
    label: 'Text',
    iconStyle: 'far',
    icon: 'text'
  },
  {
    id: 1,
    type: MatrixColumnType.NUMBER,
    label: 'Number',
    iconStyle: 'far',
    icon: 'hashtag'
  },
  {
    id: 2,
    type: MatrixColumnType.SINGLE_CHOICE,
    label: 'Single Choice',
    iconStyle: 'far',
    icon: 'dot-circle'
  },
  {
    id: 3,
    type: MatrixColumnType.MULTIPLE_CHOICE,
    label: 'Multiple choice',
    iconStyle: 'far',
    icon: 'check-square'
  },
  {
    id: 4,
    type: MatrixColumnType.DATE,
    label: 'Date',
    iconStyle: 'far',
    icon: 'calendar-alt'
  },
  {
    id: 5,
    type: MatrixColumnType.DROPDOWN,
    label: 'Dropdown',
    iconStyle: 'far',
    icon: 'square-caret-down'
  }
];

interface MatrixColumnOptions {
  mode?: string;
  placeholder?: string;
  choices?: FormFieldChoice[];
  multipleChoice?: boolean;
  format?: string;
  timeFormat?: number;
  absoluteDate?: boolean;

  dateConstraints?: { min?: number | string; max?: number | string; };
  dateShifting?: boolean;

  showingShiftedDate?: boolean;

  range?: NumberRange;
}

// eslint-disable-next-line no-shadow
export enum FormFieldEffects {
  WARN='Warn',
  SHOW='Show',
  VALID='Valid',
  ASSIGN='Assign',
  DISABLE='Disable',
  REQUIRED='Required',
  DEFAULT='Default'
}

// Field type value interfaces

export interface GeolocationPosition {
  coords: Coordinates;
  timestamp: number;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
}

export interface PositionErrorCallback {
  (positionError: GeolocationPositionError): void;
}

interface GeolocationPositionError {
  code: number;
  message: string;
  PERMISSION_DENIED: number;
  POSITION_UNAVAILABLE: number;
  TIMEOUT: number;
}

export interface Signatory {
  signature: string;
  signatory: string;
  email: string;
  dateTime: number;
  emailSent?: boolean;
  revoked?: boolean;
  removed?: boolean;
}

export interface SignatureData {
  signatories: Signatory[];
}

export interface NetworkLocationData {
  latitude: number;
  longitude: number;
  accuracy: number,
  timestamp: number;
}

// eslint-disable-next-line no-shadow
export enum DELETE_CONFIRMATION_TYPE {
  FORM='form',
  ARCHIVE='Archive'
}
